import { useEffect, useState } from 'react'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'

/**
 * Administración › Reglas de expedientes (03-nov-2026).
 *
 * «¿A quién le toca descargar un expediente completo?». Acordado con Leslie: el
 * asesor asignado. El asesor, el supervisor y el admin pueden descargar
 * SIEMPRE; esta regla decide a quién le aparece como pendiente y a quién se le
 * avisa. La guarda `CONFIG#GLOBAL` (`EXPEDIENTE_QUIEN_DESCARGA`) y solo la
 * cambia el Admin: el servidor rechaza un valor que no conoce.
 *
 * Lo que negocio todavía no definió —alertas comunes, plazo, paso siguiente—
 * no se ofrece: un control que no hace nada es peor que no tenerlo.
 */

const CLAVE = 'EXPEDIENTE_QUIEN_DESCARGA'

type Quien = 'asesor' | 'supervisor'

const OPCIONES: { valor: Quien; titulo: string; detalle: string; acordado?: boolean }[] = [
  { valor: 'asesor', titulo: 'El asesor asignado', acordado: true,
    detalle: 'Ya tiene el caso desde que se generó el checklist. Lo ve en «Mis expedientes» y le llega el aviso.' },
  { valor: 'supervisor', titulo: 'El supervisor del equipo',
    detalle: 'Los ve todos en Operación › Expedientes con «Por descargar». Al asesor no se le avisa.' },
]

/** Quién ve qué, por rol. Es lo que aplica el servidor (`portal_api`). */
const MATRIZ: { puede: string; asesor: string; supervisor: string; admin: string }[] = [
  { puede: 'Ver los suyos', asesor: 'Sí', supervisor: 'Sí', admin: 'Sí' },
  { puede: 'Ver los de todo el equipo', asesor: 'No', supervisor: 'Sí', admin: 'Sí' },
  { puede: 'Descargar y marcar descargado', asesor: 'Los suyos', supervisor: 'Sí', admin: 'Sí' },
  { puede: 'Ver el chat completo', asesor: 'Los suyos', supervisor: 'Sí', admin: 'Sí' },
  { puede: 'Reasignar', asesor: 'No', supervisor: 'Sí', admin: 'Sí' },
  { puede: 'Exportar a Excel', asesor: 'No', supervisor: 'Sí', admin: 'Sí' },
  { puede: 'Cambiar estas reglas', asesor: 'No', supervisor: 'No', admin: 'Sí' },
]

function Celda({ valor }: { valor: string }) {
  return <td className={`text-center ${valor === 'No' ? 'text-ink-faint' : 'font-bold text-brand-hover'}`}>{valor}</td>
}

export default function ReglasExpedientes() {
  const [guardado, setGuardado] = useState<Quien>('asesor')
  const [elegido, setElegido] = useState<Quien>('asesor')
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)

  useEffect(() => {
    apiClient.get<Record<string, unknown>>('/config')
      .then((res) => {
        const valor: Quien = res.data[CLAVE] === 'supervisor' ? 'supervisor' : 'asesor'
        setGuardado(valor)
        setElegido(valor)
      })
      .catch((err) => setAviso(errorMessage(err, 'No se pudo leer la regla.')))
      .finally(() => setCargando(false))
  }, [])

  const guardar = async () => {
    setGuardando(true)
    setAviso(null)
    try {
      await apiClient.put('/config', { [CLAVE]: elegido })
      setGuardado(elegido)
      setAviso('Regla guardada.')
    } catch (err) {
      setAviso(errorMessage(err, 'No se pudo guardar la regla.'))
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="flex flex-wrap items-start gap-5">
      <section className="card flex min-w-0 flex-[999_1_520px] flex-col gap-3 px-5 py-4">
        <fieldset className="flex flex-col gap-2.5" disabled={cargando || guardando}>
          <legend className="mb-1 text-[15px] font-extrabold text-ink">
            ¿A quién le toca descargar un expediente completo?
          </legend>
          <p className="text-body text-ink-muted">
            El asesor asignado y el supervisor siempre pueden descargar. Esta regla decide a quién
            le aparece como pendiente y a quién se le avisa.
          </p>
          {OPCIONES.map((o) => (
            <label key={o.valor}
                   className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 ${elegido === o.valor
                     ? 'border-brand bg-brand-selected' : 'border-surface-border'}`}>
              <input type="radio" name="quien-descarga" className="mt-1" checked={elegido === o.valor}
                     onChange={() => setElegido(o.valor)} />
              <span className="flex flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-2 text-body font-bold text-ink">
                  {o.titulo}
                  {o.acordado && <span className="badge-green">Acordado con negocio</span>}
                </span>
                <span className="text-body text-ink-muted">{o.detalle}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <p className="text-micro text-ink-muted">
          Por definir con negocio, todavía sin control: si los expedientes con alertas se revisan antes de
          descargarlos, el plazo para descargar y qué pasa después de descargar.
        </p>
        <div className="flex items-center gap-3">
          <button type="button" className="btn-primary" onClick={() => void guardar()}
                  disabled={cargando || guardando || elegido === guardado}>
            {guardando ? 'Guardando…' : 'Guardar regla'}
          </button>
          {aviso && <span className="text-body text-ink-soft" role="status">{aviso}</span>}
        </div>
      </section>

      <section className="card min-w-0 flex-[1_1_380px] px-5 py-4">
        <h2 className="text-[15px] font-extrabold text-ink">Quién ve qué</h2>
        <p className="mb-2 text-body text-ink-muted">Por rol, sobre los expedientes completos.</p>
        <div className="scrollbar-thin overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>Puede</th><th className="!text-center">Asesor</th>
                <th className="!text-center">Supervisor</th><th className="!text-center">Admin</th>
              </tr>
            </thead>
            <tbody>
              {MATRIZ.map((f) => (
                <tr key={f.puede}>
                  <th scope="row" className="!text-left font-semibold text-ink">{f.puede}</th>
                  <Celda valor={f.asesor} /><Celda valor={f.supervisor} /><Celda valor={f.admin} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
